/**
 * Library Quotes — searchable archive of every Arabic+English quote
 * pair the AI has generated. Read-only aggregator over the existing
 * BetaPostDraft store (no new backend storage). Use cases:
 *   - Re-find a quote you remember writing but can't locate
 *   - Copy the AR+EN pair to seed a new post or to use elsewhere
 *   - Spot duplicates / near-duplicates across drafts
 *
 * The list intentionally includes EVERY status (drafts, scheduled,
 * published, discarded). Discarded ones can still be inspiration —
 * the operator never loses access to the words.
 */
import { useEffect, useMemo, useState } from "react";
import { Copy, Search } from "lucide-react";
import { listBetaPostDrafts, type BetaPostDraft } from "../api";
import { PageHeader } from "../components/PageHeader";
import { useIsMobile } from "../lib/useIsMobile";

type RowSource = "draft" | "scheduled" | "published" | "discarded";

interface QuoteRow {
  id: string;
  theme: string;
  source: RowSource;
  generatedAt: number;
  arabicQuote: string;
  englishQuote: string;
  arabic: string;
  english: string;
}

function classify(s: BetaPostDraft["status"]): RowSource {
  if (s === "scheduled") return "scheduled";
  if (s === "published") return "published";
  if (s === "discarded") return "discarded";
  return "draft";
}

const SOURCE_COLOR: Record<RowSource, string> = {
  draft: "rgba(255,213,0,.18)",
  scheduled: "rgba(123,160,255,.18)",
  published: "rgba(123,211,137,.18)",
  discarded: "rgba(255,255,255,.06)",
};
const SOURCE_FG: Record<RowSource, string> = {
  draft: "var(--ql-yellow)",
  scheduled: "#7BA0FF",
  published: "#7BD389",
  discarded: "var(--ql-fg-muted)",
};

export function LibraryQuotes(): React.JSX.Element {
  const isMobile = useIsMobile();
  const [drafts, setDrafts] = useState<BetaPostDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    listBetaPostDrafts()
      .then((rows) => { if (!cancelled) setDrafts(rows); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const rows: QuoteRow[] = useMemo(() => {
    return drafts.map((d) => ({
      id: d.id,
      theme: d.theme,
      source: classify(d.status),
      generatedAt: d.generatedAt,
      arabicQuote: d.content.arabicQuote,
      englishQuote: d.content.quote,
      arabic: d.content.arabic,
      english: d.content.english,
    }));
  }, [drafts]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      r.theme.toLowerCase().includes(needle) ||
      r.englishQuote.toLowerCase().includes(needle) ||
      r.english.toLowerCase().includes(needle) ||
      r.arabicQuote.includes(needle) ||
      r.arabic.includes(needle),
    );
  }, [rows, q]);

  const copyPair = async (row: QuoteRow): Promise<void> => {
    const text = `${row.arabicQuote}\n\n${row.englishQuote}\n\n— ${row.theme}`;
    try { await navigator.clipboard.writeText(text); } catch { /* ignore */ }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Library · Quotes"
        title="Every Arabic + English pair you've written."
        subtitle="Searchable archive of every quote — drafts, scheduled, published, even discarded. Find a line and reuse it."
      />

      <div style={{
        display: "flex", alignItems: "center", gap: 8, marginBottom: 18,
        padding: "10px 14px",
        borderRadius: 12,
        border: "1px solid rgba(255,255,255,.10)",
        background: "rgba(255,255,255,.04)",
      }}>
        <Search size={14} color="var(--ql-fg-subtle)" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search Arabic, English, or theme…"
          style={{
            flex: 1,
            padding: "4px 0",
            border: 0,
            background: "transparent",
            color: "var(--ql-fg)",
            fontSize: 13,
            outline: "none",
            fontFamily: "inherit",
          }}
        />
        <span style={{
          fontSize: 10,
          color: "var(--ql-fg-subtle)",
          fontFamily: "var(--ql-font-mono)",
        }}>
          {filtered.length}/{rows.length}
        </span>
      </div>

      {error && (
        <div style={{
          marginBottom: 18, padding: "10px 14px",
          borderRadius: 12,
          background: "rgba(248,113,113,.10)",
          border: "1px solid rgba(248,113,113,.30)",
          color: "#FCA5A5", fontSize: 12,
        }}>{error}</div>
      )}

      {loading ? (
        <div style={{ color: "var(--ql-fg-muted)", padding: "40px 0", textAlign: "center" }}>
          Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div style={{
          padding: "60px 24px",
          textAlign: "center",
          border: "1px dashed rgba(255,255,255,.08)",
          borderRadius: 16,
          color: "var(--ql-fg-muted)",
          fontSize: 13,
        }}>
          {rows.length === 0 ? "No quotes yet — generate your first post." : "No matches."}
        </div>
      ) : (
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(360px, 1fr))",
          gap: 14,
        }}>
          {filtered.map((row) => (
            <div key={row.id} style={{
              padding: 16,
              borderRadius: 14,
              border: "1px solid rgba(255,255,255,.06)",
              background: "rgba(16,18,27,.6)",
              display: "flex", flexDirection: "column", gap: 10,
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <span style={{
                  fontSize: 10, fontWeight: 700,
                  letterSpacing: ".10em", textTransform: "uppercase",
                  color: SOURCE_FG[row.source],
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: SOURCE_COLOR[row.source],
                  fontFamily: "var(--ql-font-mono)",
                }}>
                  {row.source}
                </span>
                <span style={{ fontSize: 10, color: "var(--ql-fg-subtle)", fontFamily: "var(--ql-font-mono)" }}>
                  {new Date(row.generatedAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                </span>
              </div>
              <div style={{
                fontSize: 11, color: "var(--ql-fg-muted)",
                fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase",
              }}>
                {row.theme}
              </div>
              <div style={{
                fontSize: 15,
                lineHeight: 1.55,
                color: "var(--ql-fg)",
                direction: "rtl",
                textAlign: "right",
                fontFamily: "var(--ql-font-arabic)",
                fontWeight: 600,
              }}>
                {row.arabicQuote}
              </div>
              <div style={{
                fontSize: 13,
                lineHeight: 1.55,
                color: "rgba(255,255,255,.86)",
                fontStyle: "italic",
              }}>
                {row.englishQuote}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
                <button
                  type="button"
                  onClick={() => void copyPair(row)}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "5px 10px",
                    borderRadius: 8,
                    border: "1px solid rgba(255,255,255,.10)",
                    background: "rgba(255,255,255,.03)",
                    color: "var(--ql-fg-muted)",
                    fontSize: 11, fontWeight: 600,
                    cursor: "pointer", fontFamily: "inherit",
                  }}
                  title="Copy AR + EN pair to clipboard"
                >
                  <Copy size={11} strokeWidth={2} />
                  Copy pair
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
